import React, { useMemo } from 'react';
import { Moment } from 'moment';
import { useSelector } from 'react-redux';
import { dateFormats } from 'commons/src/constants';
import { BuildingType } from 'commons/src/models/commonTypeScript';
import BusinessLogo from '../../../../../img/logos/business-logo.png';
import { IaqInsightData, ReportSensorThresholds } from '../../../../../models/common';
import { Store } from '../../../../../reducers';
import getDeviceTypesFromReport from '../../getDeviceTypesFromReport';
import styles from '../../iaqInsight2.module.scss';
import { IaqInsight2PdfGenerator } from '../../pdf/IaqInsight2PdfGenerator';

export type DownloadReportPdfButtonProps = {
    building: BuildingType;
    from: Moment;
    to: Moment;
    sensorReports: { [sensorName: string]: IaqInsightData };
    thresholds: { [sensor: string]: ReportSensorThresholds };
    dateFormat: string;
    comment: string;
};

const DownloadReportPdfButton: React.FC<DownloadReportPdfButtonProps> = ({
    building,
    from,
    to,
    sensorReports,
    thresholds,
    dateFormat,
    comment,
}) => {
    const devices = useSelector((state: Store) => state.devices.devicesWithKeyInfo);
    const { userName } = useSelector((state: Store) => state.userSettings);
    const { reportId, includeLogo } = useSelector((state: Store) => state.iaqInsight);
    const logoImage = useSelector((state: Store) => state.organizationProperties.logoImage);

    const deviceTypes = useMemo(() => getDeviceTypesFromReport(sensorReports, devices), [sensorReports, devices]);

    const hasRequiredData = useMemo(() => {
        if (!building || !from || !to) return false;
        if (Object.keys(sensorReports).length === 0) return false;
        const hasAtLeastOneSensorWithDeviceData = Object.keys(sensorReports).some(sensorName => {
            const currentSensorReport = sensorReports[sensorName];
            return (
                (currentSensorReport?.spaceOpeningHoursValues || currentSensorReport?.devicesOpeningHoursValues) &&
                Object.keys(
                    currentSensorReport.spaceOpeningHoursValues || currentSensorReport.devicesOpeningHoursValues || {}
                ).length > 0
            );
        });
        return hasAtLeastOneSensorWithDeviceData;
    }, [building, from, to, sensorReports]);

    const hideCharts = useMemo(() => {
        if (!from || !to) return false;
        const daysDifference = to.diff(from, 'days') + 1;
        return daysDifference > 31;
    }, [from, to]);

    if (!hasRequiredData) {
        return null;
    }

    const dateFormatString = dateFormats[dateFormat as keyof typeof dateFormats].format;

    const getLogoToUse = (): string | undefined => {
        if (!includeLogo) return undefined;
        if (logoImage && logoImage.length > 0) return logoImage;
        return BusinessLogo;
    };
    const logoToUse = getLogoToUse();

    return (
        <div className={styles.downloadButtonWrapper}>
            <div className={styles.pdfComponentContainer}>
                <IaqInsight2PdfGenerator
                    fromDate={from.format(dateFormatString)}
                    toDate={to.format(dateFormatString)}
                    building={building}
                    reports={sensorReports}
                    thresholds={thresholds}
                    dateFormat={dateFormat as 'US' | 'EUR'}
                    devices={devices}
                    logo={logoToUse}
                    comment={comment}
                    reportDetails={{
                        deviceTypes,
                        userName: userName || '',
                        reportId: reportId || '',
                    }}
                    hideCharts={hideCharts}
                />
            </div>
        </div>
    );
};

export default DownloadReportPdfButton;
